import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, CalendarDays, Mail } from "lucide-react";
import { BookCallModal } from "@/components/BookCall";

export default function AdminDirectChat({ contactEmail }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/thread");
      setMessages(data);
    } catch { /* silent — send surfaces errors */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/support/thread", { body });
      setMessages((m) => [...m, data]);
      setInput("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="admin-direct-chat">
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-black/5">
        <div className="flex-1 leading-tight">
          <div className="text-xs font-semibold text-ink">Message your admin</div>
          <div className="text-[10px] text-ink-3">Real human — replies usually within a few hours</div>
        </div>
        <button onClick={() => setBookOpen(true)} className="org-icon-btn" title="Book a 30-min call" data-testid="chat-book-call"><CalendarDays size={14} /></button>
        {contactEmail && (
          <a href={`mailto:${contactEmail}`} className="org-icon-btn" title={`Email ${contactEmail}`} data-testid="chat-email"><Mail size={14} /></a>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="member-chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-xs text-ink-3 mt-8 px-6">
            Ask anything — wallet setup, CaryPact, your numbers. Your admin gets notified instantly.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === "member" ? "justify-end" : "justify-start"}`}>
            <div
              className="max-w-[80%] px-3 py-2 rounded-2xl text-xs leading-relaxed"
              style={m.sender_role === "member"
                ? { background: "linear-gradient(135deg, #4060FF, #6B4FFF)", color: "white", borderBottomRightRadius: 6 }
                : { background: "white", border: "1px solid rgba(15,18,38,0.08)", borderBottomLeftRadius: 6 }}
            >
              {m.sender_role === "admin" && <div className="text-[9px] font-bold mb-0.5" style={{ color: "#B91C1C" }}>ADMIN</div>}
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.body}</div>
              <div className={`text-[9px] mt-1 ${m.sender_role === "member" ? "text-white/60" : "text-ink-3"}`}>
                {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="p-2.5 border-t border-black/5 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type your question…"
          className="flex-1 text-xs px-3 py-2.5 rounded-xl outline-none"
          style={{ background: "rgba(15,18,38,0.04)", border: "1px solid rgba(15,18,38,0.07)" }}
          data-testid="member-chat-input"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className="w-9 h-9 rounded-xl text-white flex items-center justify-center disabled:opacity-40 shrink-0"
          style={{ background: "linear-gradient(135deg, #4060FF, #6B4FFF)" }}
          data-testid="member-chat-send"
        >
          <Send size={14} />
        </button>
      </div>
      <BookCallModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
}
